/**
 * Per-scenario lazy coverage store.
 *
 * Replaces the old 157MB scenario_coverage.json monolith (which parsed ALL ~12k
 * scenarios into memory and kept them resident — an OOM/freeze risk on phones).
 * Now each scenario has its own self-contained shard at
 *   /data/scenario_coverage/<scenario_id>.json  = { s, hospitals:{...}, rows:[...] }
 * containing ONLY cornerstone-qualified hospitals (cornerstone_pass &&
 * supplemental_score >= 0.5), sorted by price. We fetch one shard when a scenario
 * is opened and keep an LRU of the most-recently-used ~150 — so memory holds a few
 * MB of browsed scenarios, never all of them. Local on device, lazy, evictable.
 */
import { useState, useEffect } from 'react';
import { loadStaticData, useLiveDb } from './offlineData';

const MAX_CACHED_SCENARIOS = 150;

export interface CoverageRow {
  h: string;            // hospital_id
  hp: number;           // total_hospital_price
  np?: number | null;   // total_national_price
  mp?: number | null;   // avg_markup_percentage
  cp?: number | null;   // coverage_percentage
  mc?: number | null;   // matched_codes
  tc?: number | null;   // total_codes
  ss?: number | null;   // supplemental_score
  pm?: string;          // pricing_method: 'drg' = inpatient priced from DRG bundle (else itemized)
}

export interface HospitalInfo {
  n: string; c: string; s: string;
  la: number | null; lo: number | null;
}

// Per-scenario shard as written by generate_static_cache.py:
//   { generated_at, hospital_count, coverage: [{h, hp, cp, pm, ...}] }
// The hospital lat/lng/name lookup is NOT in the shard — it lives in the separate
// scenario_coverage_manifest.json ({ hospitals: {id: {n,c,s,la,lo}} }).
interface Shard { generated_at?: string; coverage?: CoverageRow[]; rows?: CoverageRow[]; hospitals?: Record<string, HospitalInfo>; }
interface Manifest { hospitals?: Record<string, HospitalInfo>; }

// ── In-memory state (LRU of scenarios + merged hospital lookup) ──────────────
const scenarioCache = new Map<string, CoverageRow[]>();   // insertion order = LRU order
const hospitals: Record<string, HospitalInfo> = {};
const inflight = new Map<string, Promise<CoverageRow[]>>();

// Manifest holds the hospital lookup (n/c/s/la/lo). Loaded once, lazily, the
// first time any scenario coverage is requested — getHospitalInfo depends on it.
let manifestPromise: Promise<void> | null = null;
function ensureManifest(): Promise<void> {
  if (manifestPromise) return manifestPromise;
  manifestPromise = (async () => {
    try {
      const m = await loadStaticData<Manifest>('scenario_coverage_manifest.json');
      if (m?.hospitals) Object.assign(hospitals, m.hospitals);
    } catch (e) {
      console.warn('[CoverageStore] manifest load failed', e);
    }
  })();
  return manifestPromise;
}

function touch(id: string, rows: CoverageRow[]) {
  scenarioCache.delete(id);
  scenarioCache.set(id, rows);
  while (scenarioCache.size > MAX_CACHED_SCENARIOS) {
    const oldest = scenarioCache.keys().next().value as string | undefined;
    if (oldest === undefined) break;
    scenarioCache.delete(oldest);
  }
}

/**
 * Fetch (or return cached) the qualified coverage rows for ONE scenario.
 * This is the primary entry point — call it before reading getCoverageForScenario.
 */
export async function loadScenarioCoverageFor(scenarioId: string): Promise<CoverageRow[]> {
  if (!scenarioId) return [];
  if (useLiveDb()) return [];
  const cached = scenarioCache.get(scenarioId);
  if (cached) { touch(scenarioId, cached); return cached; }
  const existing = inflight.get(scenarioId);
  if (existing) return existing;

  const p = (async () => {
    try {
      // Load manifest (hospital lookup) + the scenario shard in parallel.
      const [shard] = await Promise.all([
        loadStaticData<Shard>(`scenario_coverage/${scenarioId}.json`),
        ensureManifest(),
      ]);
      // Generator writes the rows under `coverage`; tolerate legacy `rows` too.
      const rows = shard?.coverage ?? shard?.rows ?? [];
      if (shard?.hospitals) Object.assign(hospitals, shard.hospitals); // legacy in-shard lookup, if present
      touch(scenarioId, rows);
      return rows;
    } catch (e) {
      console.warn('[CoverageStore] shard load failed for', scenarioId, e);
      touch(scenarioId, []);
      return [];
    } finally {
      inflight.delete(scenarioId);
    }
  })();
  inflight.set(scenarioId, p);
  return p;
}

/** Synchronously read already-loaded rows (call loadScenarioCoverageFor first). */
export function getCoverageForScenario(scenarioId: string): CoverageRow[] {
  return scenarioCache.get(scenarioId) || [];
}

export function getHospitalInfo(hospitalId: string): HospitalInfo | null {
  return hospitals[hospitalId] || null;
}

export function getAllHospitals(): Record<string, HospitalInfo> {
  return hospitals;
}

/** Ensure the hospital lookup manifest (n/c/s/la/lo per hospital) is loaded.
 *  Lets surfaces that only need hospital coordinates (e.g. the Leaflet map
 *  fallback) populate getHospitalInfo without fetching a scenario shard. */
export async function loadHospitalManifest(): Promise<void> {
  await ensureManifest();
}

export function isCoverageLoaded(): boolean {
  return scenarioCache.size > 0;
}

/**
 * Back-compat shim. The old API loaded the whole monolith up front; there's
 * nothing to preload now (shards load per-scenario), so this is a no-op that
 * reports success. Existing callers can keep `await loadScenarioCoverage()`.
 */
export async function loadScenarioCoverage(): Promise<boolean> {
  return true;
}

/** Haversine distance in miles between two lat/lng pairs. */
export function haversineDistance(lat1: number, lon1: number, lat2: number, lon2: number): number {
  const R = 3958.8; // miles
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) ** 2 +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

// ── Progress hooks — retained for API compatibility (CoverageProgressLoader). ──
// Per-scenario shards are tiny, so there's no long download to track; these now
// report idle/complete.
type ProgressCallback = (pct: number, done: boolean) => void;
const progressListeners = new Set<ProgressCallback>();
export function onLoadProgress(cb: ProgressCallback): () => void {
  progressListeners.add(cb);
  return () => progressListeners.delete(cb);
}
export function useScenarioCoverageProgress(): { loading: boolean; progress: number } {
  const [state] = useState({ loading: false, progress: 100 });
  useEffect(() => {}, []);
  return state;
}
